import { Movement, Location } from '../../types/movement';
import { MovementGroups } from './types';
import { calculateArrivalTime } from './timeUtils';
import { useLanguage } from '../../contexts/LanguageContext';

export const generateMovementsFromGroups = (groups: MovementGroups): Movement[] => {
  const { translations } = useLanguage();
  const movements: Movement[] = [];

  Object.entries(groups).forEach(([key, group]) => {
    const [type, fromName, toName, date, time] = key.split('|');
    const { pilgrims, package: pkg, flight } = group;

    const baseMovement = {
      id: `${type}-${date}-${time}`,
      type,
      name: getMovementName(type),
      departureDate: date,
      departureTime: time,
      estimatedHours: getEstimatedHours(type),
      pilgrimsCount: pilgrims.length
    };

    // Generate movement based on type
    switch (type) {
      case 'arrival':
        movements.push({
          ...baseMovement,
          from: { 
            type: 'airport', 
            name: fromName, 
            city: flight.outboundArrival.toLowerCase() as 'jeddah' | 'madinah' 
          },
          to: { 
            type: 'hotel', 
            name: toName, 
            city: 'makkah' 
          },
          arrivalDate: date,
          arrivalTime: calculateArrivalTime(time, baseMovement.estimatedHours)
        });
        break;

      case 'departure':
        movements.push({
          ...baseMovement,
          from: { 
            type: 'hotel', 
            name: fromName, 
            city: 'makkah' 
          },
          to: { 
            type: 'airport', 
            name: toName, 
            city: toName.toLowerCase() as 'jeddah' | 'madinah' 
          },
          arrivalDate: date,
          arrivalTime: flight.returnDepartureTime
        });
        break;

      case 'hotel_transfer':
        movements.push({
          ...baseMovement,
          from: { 
            type: 'hotel', 
            name: fromName, 
            city: 'makkah' 
          },
          to: { 
            type: 'hotel', 
            name: toName, 
            city: 'makkah' 
          },
          arrivalDate: date,
          arrivalTime: calculateArrivalTime(time, baseMovement.estimatedHours)
        });
        break;

      case 'camp_departure':
      case 'camp_return':
        movements.push({
          ...baseMovement,
          from: { 
            type: type === 'camp_departure' ? 'hotel' : 'camp', 
            name: fromName, 
            city: 'makkah' 
          },
          to: { 
            type: type === 'camp_departure' ? 'camp' : 'hotel', 
            name: toName, 
            city: 'makkah' 
          },
          arrivalDate: date,
          arrivalTime: calculateArrivalTime(time, baseMovement.estimatedHours)
        });
        break;

      case 'hotel_transfer_madinah':
        movements.push({
          ...baseMovement,
          from: { 
            type: 'hotel', 
            name: fromName, 
            city: 'makkah' 
          },
          to: { 
            type: 'hotel', 
            name: toName, 
            city: 'madinah' 
          },
          arrivalDate: date,
          arrivalTime: calculateArrivalTime(time, baseMovement.estimatedHours)
        });
        break;
    }
  });

  // Sort movements by date and time
  return movements.sort((a, b) => {
    const dateA = new Date(a.departureDate.split('/').reverse().join('-'));
    const dateB = new Date(b.departureDate.split('/').reverse().join('-'));
    return dateA.getTime() - dateB.getTime();
  });
};

const getMovementName = (type: string): string => {
  const { translations } = useLanguage();
  switch (type) {
    case 'arrival': 
      return translations?.movementTypes?.arrival || 'Pilgrims Arrival';
    case 'departure': 
      return translations?.movementTypes?.departure || 'Pilgrims Departure';
    case 'hotel_transfer': 
      return translations?.movementTypes?.hotelTransfer || 'Hotel Transfer';
    case 'camp_departure': 
      return translations?.movementTypes?.campDeparture || 'Going to Camp';
    case 'camp_return': 
      return translations?.movementTypes?.campReturn || 'Returning from Camp';
    case 'hotel_transfer_madinah': 
      return translations?.movementTypes?.hotelTransferMadinah || 'Transfer to Madinah';
    default: 
      return '';
  }
};

const getEstimatedHours = (type: string): number => {
  switch (type) {
    case 'arrival': return 2;
    case 'departure': return 2;
    case 'hotel_transfer': return 1;
    case 'camp_departure': return 1;
    case 'camp_return': return 1;
    case 'hotel_transfer_madinah': return 7;
    default: return 1;
  }
};